import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { api } from '@/lib/api';
import { useSystemConfig } from '@/lib/queries';
import { cn } from '@/lib/cn';
import { Icon } from '@/components/icons/Icon';
import { AppBar } from '@/components/ui/AppBar';
import { Card } from '@/components/ui/Card';
import { Skeleton, SkeletonText } from '@/components/ui/Skeleton';
import { ErrorState } from '@/components/ui/ErrorState';
import { Button } from '@/components/ui/Button';
import { Markdown } from '@/components/ui/Markdown';

interface PublicPage {
  slug: string;
  title: string;
  excerpt: string | null;
  body: string | null;
  updated_at: string | null;
}

/**
 * A published CMS page: terms, privacy, the returns policy.
 *
 * Public, because §4.5 requires the returns policy readable at checkout and a
 * policy behind a login is not a policy. The "last updated" line is deliberate:
 * a legal page changing quietly is exactly the problem revisions exist to
 * solve, and showing the date is the cheap half of that.
 */
export function PageScreen() {
  const { slug } = useParams<{ slug: string }>();

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['page', slug],
    queryFn: ({ signal }) =>
      api
        .request<PublicPage>(`/pages/${slug}`, { signal })
        .then((response) => response.data),
    enabled: Boolean(slug),
  });

  if (isError) {
    return (
      <>
        <AppBar back title="Page" />
        <ErrorState
          title="We couldn't find that page"
          body="The link may be old, or the page may not be published yet."
          action={
            <Button icon="refresh" onClick={() => refetch()}>
              Try again
            </Button>
          }
        />
      </>
    );
  }

  return (
    <>
      <AppBar back title={data?.title ?? ' '} />

      <article className="mx-auto w-full max-w-[760px] px-4 py-6 xl:px-6">
        {isLoading || !data ? (
          <Card>
            <Skeleton className="h-7 w-2/3" />
            <div className="mt-5 space-y-5">
              <SkeletonText lines={4} />
              <SkeletonText lines={3} />
            </div>
          </Card>
        ) : (
          <Card className="!p-6 xl:!p-9">
            <h1 className="m-0 font-display text-[26px] font-bold leading-tight tracking-[-0.02em] xl:text-[32px]">
              {data.title}
            </h1>

            {data.excerpt ? <p className="mt-2 text-[14px] text-muted">{data.excerpt}</p> : null}

            {data.updated_at ? (
              <p className="mt-3 font-mono text-[11px] text-faint">
                Last updated{' '}
                {new Date(data.updated_at).toLocaleDateString('en-NG', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                })}
              </p>
            ) : null}

            <hr className="my-6 border-0 border-t border-line-2" />

            <Markdown source={data.body ?? ''} />

            <ApplyBlock slug={data.slug} />
          </Card>
        )}
      </article>
    </>
  );
}

/**
 * The way in, at the bottom of the page that argues for it.
 *
 * ── THE GAP THIS CLOSES ────────────────────────────────────────
 *
 * "Read full details" on the account screen led here, and here was the end of
 * the road: payout timing, commission bands, rider liability — a complete
 * commercial case with nothing to click. Somebody persuaded by it had to go
 * back two screens and find the row they had just left.
 *
 * So the page now ends with both doors. Pharmacy and veterinary are separate
 * applications because they are separate regulators — PCN premises licensing on
 * one side, VCN on the other — and the review that follows is a different
 * review. Anyone who is both ticks both.
 *
 * ── AND WHY IT SAYS "REVIEWED" IN SO MANY WORDS ───────────────────────
 *
 * Submitting does not open a shop. The licence is checked by hand against the
 * register before a single pack can be listed, and a seller who expected to be
 * trading that afternoon is a seller who chases support instead. Saying it on
 * the form is cheaper than explaining it afterwards, and it is the honest
 * description of what happens.
 *
 * ── AND WHY IT ONLY APPEARS ON ONE PAGE ─────────────────────────────
 *
 * It is keyed off `seller_pitch.page_slug` — the same setting the account rows
 * and the sell card already read to find this page. So an admin who points the
 * pitch at a different page moves these buttons with it, and the returns policy
 * and the privacy notice never sprout a recruitment form.
 *
 * Each door is shown only if its own pitch heading is set, which is how one
 * side of the drive is switched off without touching the other — the same rule
 * PartnerRows follows on the account screen.
 */
function ApplyBlock({ slug }: { slug: string }) {
  const { data: config } = useSystemConfig();
  const pitch = config?.seller_pitch;

  if (!pitch?.page_slug?.trim() || pitch.page_slug.trim() !== slug) return null;

  // The labels are settings. The `note` under each one is NOT, and that is a
  // deliberate exception to the rule that customer copy lives in admin: it
  // names the regulator each application is checked against, which is a fact
  // about Nigerian law rather than wording to be tuned. It is also the single
  // most useful line for the one reader who genuinely does not know which door
  // is theirs — a clinic with a retail counter, a pharmacy that stocks
  // dewormers. If it ever needs rewording it becomes two more settings.
  const doors = [
    {
      key: 'pharmacy',
      to: '/sell',
      label: pitch.card_cta?.trim() || 'Register your pharmacy',
      note: 'Human health · PCN premises licence',
      show: Boolean(pitch.card_heading?.trim()),
    },
    {
      key: 'veterinary',
      to: '/sell?sector=veterinary',
      label: pitch.vet_card_cta?.trim() || 'Register your vet practice',
      note: 'Animal health · VCN registration',
      show: Boolean(pitch.vet_card_heading?.trim()),
    },
  ].filter((door) => door.show);

  if (doors.length === 0) return null;

  // Both lines are admin-owned, like every other word of the recruitment copy.
  // The people on the phone to pharmacies and agrovet stores find out what
  // persuades them, and they change it in Admin › Settings without a deploy.
  const heading = pitch.apply_heading?.trim();
  const note = pitch.apply_body?.trim();

  return (
    <section
      data-testid="apply-block"
      className="mt-8 rounded-card border border-blue bg-blue-tint p-5"
    >
      {heading ? (
        <h2 className="m-0 font-display text-[17px] font-bold tracking-[-0.015em]">{heading}</h2>
      ) : null}

      {note ? (
        <p className="m-0 mt-1.5 text-[13px] leading-relaxed text-ink">{note}</p>
      ) : null}

      <div className={cn('flex flex-col gap-2.5 sm:flex-row', heading || note ? 'mt-4' : '')}>
        {doors.map((door) => (
          <Link
            key={door.key}
            to={door.to}
            data-testid={`apply-${door.key}`}
            className="flex min-h-11 flex-1 items-center gap-2 rounded-btn bg-blue px-4 py-2.5 no-underline"
          >
            <span className="min-w-0 flex-1">
              <span className="block text-[13.5px] font-bold text-white">{door.label}</span>
              <span className="mt-0.5 block font-mono text-[10.5px] uppercase tracking-[0.08em] text-white/70">
                {door.note}
              </span>
            </span>

            <Icon name="chevron" size={15} strokeWidth={2} className="shrink-0 text-white/80" />
          </Link>
        ))}
      </div>
    </section>
  );
}
